import { supabase } from '@/integrations/supabase/client';
import { Employee, StandardEmployeeSelection, ContractStatus, ContractType } from '@/types';
import { differenceInMinutes, isWithinInterval } from 'date-fns';
import { isPlanningPreviewId } from '@/lib/scheduling/shift-utils';

// Service for centralizing all employee selection business logic
export class EmployeeSelectionService {
  // Permission checking logic based on actual database fields
  static canEmployeeWorkShifts(employee: Partial<Employee>): boolean {
    // Check if employment type allows shift work
    // Most contract types allow shifts, except for terminated or specific restrictions
    const contractType = employee.contract_type || employee.employment_unified_type;
    
    // If no contract type info, assume can work shifts (backward compatibility)
    if (!contractType) {
      return true;
    }
    
    // Generally all active employment types can work shifts
    // This can be expanded with specific business rules if needed
    return true;
  }

  static isEmployeeEligibleForPayroll(employee: Partial<Employee>): boolean {
    // Check if employee has payroll-relevant information
    const contractType = employee.contract_type || employee.employment_unified_type;
    
    // If employee has contract information or hourly rate, they're likely payroll eligible
    if (contractType || employee.hourly_rate || employee.salary_adjustment) {
      return true;
    }
    
    // Default to true for backward compatibility
    return true;
  }

  static isEmployeeActive(employee: Partial<Employee>): boolean {
    // Since contract_status doesn't exist in database, use contract presence as activity indicator
    const contractType = employee.contract_type || employee.employment_unified_type;
    
    // If employee has contract info and valid dates, consider active
    if (contractType) {
      const now = new Date();
      const startDate = employee.contract_start_date ? new Date(employee.contract_start_date) : null;
      const endDate = employee.contract_end_date ? new Date(employee.contract_end_date) : null;
      
      // Check if within contract period
      if (startDate && startDate > now) {
        return false; // Contract hasn't started yet
      }
      
      if (endDate && endDate < now) {
        return false; // Contract has ended
      }
      
      return true;
    }
    
    // Default to true if no contract info (backward compatibility)
    return true;
  }

  // Role-based filtering
  static hasRequiredRole(employee: Partial<Employee>, requiredRoles: string[]): boolean {
    if (!requiredRoles.length) return true;
    
    return employee.role_id ? requiredRoles.includes(employee.role_id) : false;
  }

  // Contract type filtering
  static hasValidContractType(employee: Partial<Employee>, validContractTypes: ContractType[]): boolean {
    if (!validContractTypes.length) return true;
    
    const employeeType = employee.contract_type || employee.employment_unified_type;
    return employeeType ? validContractTypes.includes(employeeType as ContractType) : false;
  }

  // Availability checking logic
  static async checkEmployeeAvailability(
    employeeId: string,
    proposedStartTime: Date,
    proposedEndTime: Date,
    excludeShiftId?: string
  ): Promise<{ isAvailable: boolean; conflicts: any[] }> {
    try {

      // Build the base query for overlapping shifts
      // Two ranges overlap if: (start1 < end2) AND (start2 < end1)
      // In SQL: (proposed_start < existing_end) AND (existing_start < proposed_end)
      let query = supabase
        .from('shifts')
        .select('id, title, start_time, end_time, employee_id')
        .eq('employee_id', employeeId)
        .lt('start_time', proposedEndTime.toISOString())  // existing_start < proposed_end
        .gt('end_time', proposedStartTime.toISOString()); // existing_end > proposed_start

      // Only add .neq filter for real shift IDs - preview IDs contain special chars
      // that break PostgREST and don't exist in the database anyway
      if (excludeShiftId && excludeShiftId.trim() && !isPlanningPreviewId(excludeShiftId)) {
        query = query.neq('id', excludeShiftId);
      }

      const { data: conflicts, error } = await query;


      if (error) {
        return { isAvailable: false, conflicts: [] };
      }

      const hasConflicts = conflicts && conflicts.length > 0;
      const result = {
        isAvailable: !hasConflicts,
        conflicts: conflicts || []
      };


      return result;
    } catch (error) {
      return { isAvailable: false, conflicts: [] };
    }
  }

  // Batch availability checking for multiple employees
  static async checkMultipleEmployeeAvailability(
    employeeIds: string[],
    proposedStartTime: Date,
    proposedEndTime: Date,
    excludeShiftId?: string
  ): Promise<Record<string, { isAvailable: boolean; conflicts: any[] }>> {
    try {

      // Build the base query for overlapping shifts using correct overlap logic
      // Two ranges overlap if: (start1 < end2) AND (start2 < end1)
      let query = supabase
        .from('shifts')
        .select('id, title, start_time, end_time, employee_id')
        .in('employee_id', employeeIds)
        .lt('start_time', proposedEndTime.toISOString())  // existing_start < proposed_end
        .gt('end_time', proposedStartTime.toISOString()); // existing_end > proposed_start

      // Only add .neq filter for real shift IDs - preview IDs contain special chars
      // that break PostgREST and don't exist in the database anyway
      if (excludeShiftId && excludeShiftId.trim() && !isPlanningPreviewId(excludeShiftId)) {
        query = query.neq('id', excludeShiftId);
      }

      const { data: allConflicts, error } = await query;


      if (error) {
        const result: Record<string, { isAvailable: boolean; conflicts: any[] }> = {};
        employeeIds.forEach(id => {
          result[id] = { isAvailable: false, conflicts: [] };
        });
        return result;
      }

      // Group conflicts by employee
      const conflictsByEmployee: Record<string, any[]> = {};
      allConflicts?.forEach(conflict => {
        if (!conflictsByEmployee[conflict.employee_id]) {
          conflictsByEmployee[conflict.employee_id] = [];
        }
        conflictsByEmployee[conflict.employee_id].push(conflict);
      });

      // Build result for each employee
      const result: Record<string, { isAvailable: boolean; conflicts: any[] }> = {};
      employeeIds.forEach(employeeId => {
        const conflicts = conflictsByEmployee[employeeId] || [];
        result[employeeId] = {
          isAvailable: conflicts.length === 0,
          conflicts
        };
        
      });

      return result;
    } catch (error) {
      const result: Record<string, { isAvailable: boolean; conflicts: any[] }> = {};
      employeeIds.forEach(id => {
        result[id] = { isAvailable: false, conflicts: [] };
      });
      return result;
    }
  }

  // Search and filtering logic
  static filterEmployeesBySearch(
    employees: StandardEmployeeSelection[],
    searchTerm: string,
    searchFields: ('name' | 'email' | 'role')[] = ['name', 'email']
  ): StandardEmployeeSelection[] {
    if (!searchTerm.trim()) return employees;

    const searchLower = searchTerm.toLowerCase();
    return employees.filter(employee => {
      return searchFields.some(field => {
        switch (field) {
          case 'name':
            return employee.name?.toLowerCase().includes(searchLower);
          case 'email':
            return employee.email?.toLowerCase().includes(searchLower);
          case 'role':
            return employee.roleInfo?.name?.toLowerCase().includes(searchLower);
          default:
            return false;
        }
      });
    });
  }

  // Sorting logic
  static sortEmployees(
    employees: StandardEmployeeSelection[],
    sortBy: 'name' | 'role' | 'created_at' = 'name',
    sortOrder: 'asc' | 'desc' = 'asc'
  ): StandardEmployeeSelection[] {
    return [...employees].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortBy) {
        case 'role':
          aValue = a.roleInfo?.name || '';
          bValue = b.roleInfo?.name || '';
          break;
        case 'created_at':
          // Assuming we have created_at on StandardEmployeeSelection or fallback to name
          aValue = (a as any).created_at || a.name;
          bValue = (b as any).created_at || b.name;
          break;
        case 'name':
        default:
          aValue = a.name;
          bValue = b.name;
          break;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortOrder === 'asc' ? comparison : -comparison;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Combined filtering logic
  static applyFilters(
    employees: StandardEmployeeSelection[],
    filters: {
      searchTerm?: string;
      searchFields?: ('name' | 'email' | 'role')[];
      includeInactive?: boolean;
      requireShiftEligibility?: boolean;
      requirePayrollEligibility?: boolean;
      filterByRole?: string[];
      filterByContractStatus?: ContractStatus[];
      sortBy?: 'name' | 'role' | 'created_at';
      sortOrder?: 'asc' | 'desc';
    }
  ): StandardEmployeeSelection[] {
    let filtered = [...employees];

    // Apply active/inactive filter
    if (!filters.includeInactive) {
      filtered = filtered.filter(emp => this.isEmployeeActive(emp));
    }

    // Apply shift eligibility filter
    if (filters.requireShiftEligibility) {
      filtered = filtered.filter(emp => this.canEmployeeWorkShifts(emp));
    }

    // Apply payroll eligibility filter
    if (filters.requirePayrollEligibility) {
      filtered = filtered.filter(emp => this.isEmployeeEligibleForPayroll(emp));
    }

    // Apply role filter
    if (filters.filterByRole && filters.filterByRole.length > 0) {
      filtered = filtered.filter(emp => this.hasRequiredRole(emp, filters.filterByRole!));
    }

    // Apply contract type filter (using available contract_type field)
    if (filters.filterByContractStatus && filters.filterByContractStatus.length > 0) {
      filtered = filtered.filter(emp => {
        const contractType = emp.contract_type || emp.employment_unified_type;
        return contractType && filters.filterByContractStatus!.includes(contractType as any);
      });
    }

    // Apply search filter
    if (filters.searchTerm) {
      filtered = this.filterEmployeesBySearch(filtered, filters.searchTerm, filters.searchFields);
    }

    // Apply sorting
    if (filters.sortBy) {
      filtered = this.sortEmployees(filtered, filters.sortBy, filters.sortOrder);
    }

    return filtered;
  }

  // Validation logic
  static validateEmployeeSelection(
    selectedIds: string[],
    employees: StandardEmployeeSelection[],
    config: {
      mode: 'single' | 'multiple';
      required?: boolean;
      maxSelection?: number;
      requireActiveStatus?: boolean;
      requireShiftEligibility?: boolean;
    }
  ): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check selection count
    if (config.required && selectedIds.length === 0) {
      errors.push('At least one employee must be selected');
    }

    if (config.mode === 'single' && selectedIds.length > 1) {
      errors.push('Only one employee can be selected');
    }

    if (config.maxSelection && selectedIds.length > config.maxSelection) {
      errors.push(`Maximum ${config.maxSelection} employees can be selected`);
    }

    // Check selected employees exist and meet requirements
    const selectedEmployees = employees.filter(emp => selectedIds.includes(emp.id));
    
    if (selectedEmployees.length !== selectedIds.length) {
      errors.push('Some selected employees are no longer available');
    }

    // Check status requirements
    if (config.requireActiveStatus) {
      const inactiveCount = selectedEmployees.filter(emp => !this.isEmployeeActive(emp)).length;
      if (inactiveCount > 0) {
        errors.push(`${inactiveCount} selected employee(s) are not active`);
      }
    }

    if (config.requireShiftEligibility) {
      const ineligibleCount = selectedEmployees.filter(emp => !this.canEmployeeWorkShifts(emp)).length;
      if (ineligibleCount > 0) {
        errors.push(`${ineligibleCount} selected employee(s) cannot work shifts`);
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Utility methods for common business rules
  static canAssignToShift(employee: Partial<Employee>): boolean {
    return this.isEmployeeActive(employee) && this.canEmployeeWorkShifts(employee);
  }

  static canIncludeInPayroll(employee: Partial<Employee>): boolean {
    return this.isEmployeeActive(employee) && this.isEmployeeEligibleForPayroll(employee);
  }

  static canParticipateInCommunications(employee: Partial<Employee>): boolean {
    // Communications typically just require active status
    return this.isEmployeeActive(employee);
  }

  static canAttendEvents(employee: Partial<Employee>): boolean {
    // Events can include active employees
    return this.isEmployeeActive(employee);
  }

  // Data transformation utilities
  static transformToStandardFormat(employee: any): StandardEmployeeSelection {
    return {
      id: employee.id,
      name: employee.name,
      email: employee.email,
      role_id: employee.role_id,
      roleInfo: employee.roleInfo || employee.roles ? {
        name: employee.roleInfo?.name || employee.roles?.name || '',
        default_pay_rate: employee.roleInfo?.default_pay_rate || 0,
        pay_type: employee.roleInfo?.pay_type || 'hourly',
      } : undefined,
      // Map actual database fields
      contract_type: employee.contract_type,
      employment_unified_type: employee.employment_unified_type,
      contract_start_date: employee.contract_start_date,
      contract_end_date: employee.contract_end_date,
      hourly_rate: employee.hourly_rate,
    };
  }

  // Query building utilities — queries employee_assignments with employee join
  static buildEmployeeQuery(
    businessId: string,
    dataFormat: 'base' | 'standard' | 'communication' | 'advanced' = 'standard'
  ) {
    const baseQuery = supabase.from('employee_assignments').eq('business_id', businessId);

    switch (dataFormat) {
      case 'base':
        return baseQuery.select(`
          id, business_id,
          employee:employees!employee_id(id, name, email)
        `);
      case 'communication':
        return baseQuery.select(`
          id, business_id, role_id,
          employee:employees!employee_id(id, name, email, user_id, created_at),
          roleInfo:roles!role_id(id, name)
        `);
      case 'advanced':
        return baseQuery.select(`
          id, business_id, access_level, role_id, app_role,
          employment_type_id, contract_type, employment_unified_type, contract_start_date, contract_end_date,
          hourly_rate, salary_adjustment, dotypos_employee_id, created_at, updated_at,
          employee:employees!employee_id(
            id, name, email, user_id, created_at,
            children:employee_children(*),
            garnishments:employee_garnishments(*)
          ),
          positionInfo:roles!role_id(id, name, display_name, default_pay_rate, pay_type, hours_per_month, business_id),
        `);
      case 'standard':
      default:
        return baseQuery.select(`
          id, business_id, role_id,
          contract_type, employment_unified_type, contract_start_date, contract_end_date, hourly_rate,
          employee:employees!employee_id(id, name, email),
          positionInfo:roles!role_id(name, display_name, default_pay_rate, pay_type),
        `);
    }
  }
}